import React, { useEffect, useRef } from 'react';

const COLORS = [
  '#FFD700',
  '#FF6B6B',
  '#4ECDC4',
  '#61DAFB',
  '#A78BFA',
  '#F59E0B',
];

function createRainParticle(canvasWidth, canvasHeight) {
  return {
    x: Math.random() * canvasWidth,
    y: -20,
    size: 6 + Math.random() * 6,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 8,
    velocityX: (Math.random() - 0.5) * 3,
    velocityY: 2 + Math.random() * 4,
    life: 0,
    maxLife: 200 + Math.random() * 200,
  };
}

function createSideCannonParticle(canvasWidth, canvasHeight) {
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -20 : canvasWidth + 20;
  const y = canvasHeight * (0.7 + Math.random() * 0.2); // near bottom sides
  const baseSpeed = 7 + Math.random() * 4; // 7 - 11
  const velocityX = fromLeft ? baseSpeed : -baseSpeed;
  const velocityY = -(2 + Math.random() * 5); // upward initial burst
  return {
    x,
    y,
    size: 6 + Math.random() * 8,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 10,
    velocityX: velocityX + (Math.random() - 0.5) * 2, // small horizontal spread
    velocityY: velocityY + (Math.random() - 0.5) * 1.5, // small vertical spread
    life: 0,
    maxLife: 240 + Math.random() * 240,
  };
}

// Fireworks helpers
function createFireworkRocket(canvasWidth, canvasHeight) {
  const x = canvasWidth * (0.1 + Math.random() * 0.8);
  const y = canvasHeight + 10;
  const horizontalDrift = (Math.random() - 0.5) * 2.0;
  const initialSpeedY = -10 - Math.random() * 6; // upward
  return {
    x,
    y,
    size: 3 + Math.random() * 2,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    velocityX: horizontalDrift,
    velocityY: initialSpeedY,
    life: 0,
    maxLife: 200,
    exploded: false,
  };
}

function createFireworkSpark(x, y, baseColor) {
  const angle = Math.random() * Math.PI * 2;
  const speed = 2 + Math.random() * 5;
  return {
    x,
    y,
    size: 2 + Math.random() * 2,
    color: baseColor,
    velocityX: Math.cos(angle) * speed,
    velocityY: Math.sin(angle) * speed,
    life: 0,
    maxLife: 70 + Math.random() * 60,
    alpha: 1,
  };
}

const Celebration = ({
  active = false,
  durationMs = 6000,
  intensity = 160,
  mode = 'side-cannons', // 'side-cannons' | 'rain' | 'fireworks'
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particles = [];
    let rockets = [];
    let sparks = [];

    function resizeCanvas() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    function drawParticle(p) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      ctx.restore();
    }

    function updateParticle(
      p,
      gravity = mode === 'side-cannons' ? 0.12 : 0.05,
      drag = 0.993
    ) {
      p.velocityY += gravity;
      p.velocityX *= drag;
      p.x += p.velocityX;
      p.y += p.velocityY;
      p.rotation += p.rotationSpeed;
      p.life += 1;
    }

    function spawnParticles(count) {
      for (let i = 0; i < count; i += 1) {
        if (mode === 'side-cannons') {
          particles.push(createSideCannonParticle(canvas.width, canvas.height));
        } else {
          particles.push(createRainParticle(canvas.width, canvas.height));
        }
      }
    }

    function updateAndDrawFireworks(elapsed) {
      // Spawn rockets during first 75% of duration
      if (elapsed < durationMs * 0.75) {
        const rocketsPerFrame = Math.max(1, Math.floor(intensity / 140));
        for (let i = 0; i < rocketsPerFrame; i += 1) {
          if (Math.random() < 0.5 && rockets.length < 20) {
            rockets.push(createFireworkRocket(canvas.width, canvas.height));
          }
        }
      }

      // Update rockets
      const gravityRocket = 0.08;
      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const r = rockets[i];
        r.velocityY += gravityRocket;
        r.x += r.velocityX;
        r.y += r.velocityY;
        r.life += 1;

        const reachedApex = r.velocityY >= -0.5;
        const maxHeightReached =
          r.y < canvas.height * (0.25 + Math.random() * 0.15);
        const timeExceeded = r.life > r.maxLife;
        if (!r.exploded && (reachedApex || maxHeightReached || timeExceeded)) {
          r.exploded = true;
          // Create sparks
          const sparkCount =
            30 + Math.floor(Math.random() * 40) + Math.floor(intensity / 8);
          for (let s = 0; s < sparkCount; s += 1) {
            sparks.push(createFireworkSpark(r.x, r.y, r.color));
          }
          rockets.splice(i, 1);
        }
        // Cull off-screen rockets
        if (r.y < -50 || r.y > canvas.height + 50) {
          rockets.splice(i, 1);
        }
      }

      // Update sparks
      const gravitySpark = 0.06;
      const dragSpark = 0.985;
      for (let i = sparks.length - 1; i >= 0; i -= 1) {
        const sp = sparks[i];
        sp.velocityY += gravitySpark;
        sp.velocityX *= dragSpark;
        sp.x += sp.velocityX;
        sp.y += sp.velocityY;
        sp.life += 1;
        sp.alpha = Math.max(0, 1 - sp.life / sp.maxLife);

        // Draw spark with fading
        ctx.save();
        ctx.globalAlpha = sp.alpha;
        ctx.fillStyle = sp.color;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, sp.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (sp.life >= sp.maxLife || sp.y > canvas.height + 40) {
          sparks.splice(i, 1);
        }
      }
    }

    function loop(now) {
      if (!startTimeRef.current) startTimeRef.current = now;
      const elapsed = now - startTimeRef.current;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (mode === 'fireworks') {
        updateAndDrawFireworks(elapsed);
      } else {
        // Spawn new particles during the first portion of duration
        if (mode === 'side-cannons') {
          if (elapsed < durationMs * 0.75) {
            // More per frame for cannon effect
            spawnParticles(Math.max(6, Math.floor(intensity / 20)));
          }
        } else {
          if (elapsed < durationMs * 0.6) {
            spawnParticles(Math.max(4, Math.floor(intensity / 30)));
          }
        }

        particles.forEach((p) => updateParticle(p));
        particles = particles.filter(
          (p) => p.y < canvas.height + 20 && p.life < p.maxLife
        );
        particles.forEach((p) => drawParticle(p));
      }

      const stillAnimating =
        elapsed < durationMs ||
        particles.length > 0 ||
        rockets.length > 0 ||
        sparks.length > 0;

      if (stillAnimating) {
        animationRef.current = requestAnimationFrame(loop);
      }
    }

    if (active) {
      startTimeRef.current = 0;
      animationRef.current = requestAnimationFrame(loop);
    }

    return () => {
      window.cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [active, durationMs, intensity, mode]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />
  );
};

export default Celebration;
